import DeleteIcon from "assets/icons/mono/DeleteIcon";
import EditIcon from "assets/icons/mono/EditIcon";
import UserIcon2 from "assets/icons/mono/UserIcon2";
import { PRIVILEGE } from "constants/privilege";
import withAccessControl from "hoc/withAccessControl";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useCallback, useEffect } from "react";
import Avatar from "react-avatar";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { deleteUser as deleteUserAPI } from "services/api/users";
import { fetchUserList } from "slices/users/list/thunk";
import { AppDispatch, RootState } from "store"
import Swal from "sweetalert2";
import { User } from "types/users";
import { getErrorMessage } from "utils/helpers";
import AccessControl from "wrappers/AccessControl";
import InfiniteScroll from "react-infinite-scroll-component";

const Index = () => {

    useDocumentTitle("Users");

    const dispatch: AppDispatch = useDispatch();
    const currentUser = useSelector((state: RootState) => state.auth.user);
    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency)

    const { items: users, loading, has_more, page, error } = useSelector((state: RootState) => state.userList);

    const fetchUsers = useCallback(async (page: number, force: boolean = false) => {
        dispatch(fetchUserList({ page, force }));
    }, [dispatch])

    useEffect(() => {
        fetchUsers(1);
    }, [fetchUsers])

    const deleteUser = async (user: User) => {

        if (currentUser?.uuid === user.uuid) return;

        const result = await Swal.fire({
            title: 'Are you sure you want to delete this user?',
            text: "there is no undo after deleting the user",
            iconHtml: `<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="40" cy="40" r="40" fill="#F1F5F4" />
            <path d="M43 47L31 48L26 51C26 52.6667 26.2 56.2 27 57C27.8 57.8 34.6667 58.6667 38 59L51 57L54 53L52 49L43 47Z" fill="#BBD8ED" />
            <path fillRule="evenodd" clipRule="evenodd" d="M39.9699 46.6875C32.2346 46.6875 25.6289 47.857 25.6289 52.5408C25.6289 57.2246 32.1927 58.4361 39.9699 58.4361C47.7051 58.4361 54.3089 57.2646 54.3089 52.5827C54.3089 47.9008 47.747 46.6875 39.9699 46.6875Z" stroke="#00A7E4" strokeLinecap="round" strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd" d="M39.9698 40.0079C45.046 40.0079 49.1603 35.8917 49.1603 30.8155C49.1603 25.7393 45.046 21.625 39.9698 21.625C34.8936 21.625 30.7774 25.7393 30.7774 30.8155C30.7603 35.8745 34.8479 39.9907 39.905 40.0079H39.9698Z" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
        </svg>`,
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "No"
        });

        if (result.isConfirmed) {
            try {
                await deleteUserAPI(selectedAgency?.uuid!, user.uuid);
                toast.success("User removed successfully!");
                fetchUsers(1, true);
            } catch (error) {
                console.log(error);
                toast.error(getErrorMessage(error, "Failed to remove user"));
            }
        }
    }

    const nextPage = () => {
        if (has_more) {
            const nextPageNumber = page + 1;
            fetchUsers(nextPageNumber);
        }
    }

    return (
        <div className="page">
            <div className="page-heading">
                <div className="content pt-0 d-flex w-100 justify-content-between align-items-center">
                    <h4 className="page-title">Users</h4>
                    <AccessControl privilege={PRIVILEGE.AGENCY.USER.CREATE}>
                        <Link to={{ pathname: '/user/create' }} className="theme-btn theme-btn-md link-as-btn btn-gradient"><UserIcon2 className="me-2" /> Add New User</Link>
                    </AccessControl>
                </div>
            </div>
            <div className="content">
                {
                    users.length > 0 &&
                    <div className="card">
                        <div className="card-body p-0 m-0 table-responsive">
                            <InfiniteScroll
                                dataLength={users.length}
                                next={nextPage}
                                hasMore={has_more}
                                loader={<></>}
                                className="w-100"
                            >
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Name <i className="icon-Avg-Like"></i></th>
                                            <th className="text-start px-5">Email</th>
                                            <th>Role</th>
                                            <th></th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            users.map((user, i) => {
                                                return (
                                                    <tr className="border-bottom-1 px-3" key={i}>
                                                        <td className="w-25">
                                                            <div className="py-4">
                                                                <div className="d-flex justify-content-start align-items-center gap-3 border-right-dashed td-content">
                                                                    <Avatar size='36' round={'4px'} name={user?.name} />
                                                                    <span className="user-name">{user.name}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="">
                                                            <div className="py-4">
                                                                <div className="d-flex justify-content-start align-items-center gap-2 border-right-dashed td-content px-5">
                                                                    <span className="">{user.email}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="py-4">
                                                                <div className="d-flex justify-content-center align-items-center gap-2 border-right-dashed td-content">
                                                                    <span className="">{user.role.name}</span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="table-action-column">
                                                            <div className="py-4">
                                                                <div className="d-flex justify-content-center align-items-center gap-2 border-right-dashed td-content">
                                                                    {
                                                                        currentUser?.uuid !== user.uuid &&
                                                                        <AccessControl privilege={PRIVILEGE.AGENCY.USER.EDIT}>
                                                                            <Link to={`/user/${user.uuid}/edit`}><EditIcon /></Link>
                                                                        </AccessControl>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="table-action-column">
                                                            <div className="py-4">
                                                                <div className="d-flex justify-content-center align-items-center gap-2 td-content">
                                                                    {
                                                                        currentUser?.uuid !== user.uuid &&
                                                                        <AccessControl privilege={PRIVILEGE.AGENCY.USER.DESTROY}>
                                                                            <span className="cursor-pointer" onClick={() => deleteUser(user)}><DeleteIcon /> </span>
                                                                        </AccessControl>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        }
                                        {
                                            loading && Array.from({ length: 10 }).map((_, i) => {
                                                return (
                                                    <tr>
                                                        <td><Skeleton /></td>
                                                        <td><Skeleton /></td>
                                                        <td><Skeleton /></td>
                                                        <td><Skeleton /></td>
                                                        <td><Skeleton /></td>
                                                    </tr>
                                                )
                                            })
                                        }
                                        {
                                            !loading && error &&
                                            <tr>
                                                <td colSpan={5}>
                                                    <div className="d-flex py-3 justify-content-center align-items-center">
                                                        <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        }
                                    </tbody>
                                </table>
                            </InfiniteScroll>
                        </div>
                    </div>
                }
                {
                    !loading && users.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center">
                        <div className="length-zero">
                            <h3>Currently there is no user in your list</h3>
                        </div>
                    </div>
                }
            </div>
        </div>

    )
}

export default withAccessControl(Index, PRIVILEGE.AGENCY.USER.INDEX)